import classNames from 'classnames';
import React, { FC, memo } from 'react';
import SizeCodeDefaultImage from 'shared/assets/images/SizeCodeDefaultImage.png';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { SizeCodeType, SizeGroup } from 'entities/SizeCode';
import { RentOptionDocument } from 'entities/RentOption';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { ContactUs } from './ContactUs';
import { getBoxDiscountedPriceAmount, getBoxPriceAmountWithVat } from 'entities/Box';
import { getWarehouseInfo } from 'pages/BookingPage';
import { Trans } from 'react-i18next';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { UserAccountType } from 'entities/User';
import { roundNumber } from 'shared/utils/helpers/roundNumber';

interface CollapseHeadingProps {
  square: number;
  availableBoxesCount: number;
  rentDiscounts: RentOptionDocument[];
  generalDiscounts: RentOptionDocument[];
  isOpened: boolean;
  imageUrl?: string;
  description?: string;
  sizeGroup?: SizeGroup;
  sizeCodeType?: SizeCodeType;
  minBoxPrice?: number;
  accountType?: Nullable<UserAccountType>;
}

export const CollapseHeading: FC<CollapseHeadingProps> = memo((props) => {
  const {
    square,
    description,
    imageUrl,
    availableBoxesCount,
    sizeGroup,
    sizeCodeType,
    rentDiscounts,
    generalDiscounts,
    minBoxPrice,
    isOpened,
    accountType,
  } = props;

  const { t } = useAppTranslation('booking');

  const globalSettings = useAppSelector(getGlobalSettings);

  const warehouseInfo = useAppSelector(getWarehouseInfo);

  const currencySymbol = useGetCurrencySymbol();

  const discountedPrice = getBoxDiscountedPriceAmount({
    warehouseDiscounts: warehouseInfo?.warehouseDiscounts,
    boxPrice: minBoxPrice,
    boxSquare: square,
  });

  const discountedBoxPriceWithVat = getBoxPriceAmountWithVat(discountedPrice, warehouseInfo, accountType);

  const discountedBoxPrice = roundNumber(globalSettings?.showPriceWithVat ? discountedBoxPriceWithVat : discountedPrice);

  const { isDesktop, isMobile } = useScreenBreakpoints();

  const noAvailableBoxes = availableBoxesCount < 1;

  const disabledElement = { 'opacity-30': noAvailableBoxes };

  const hasRentDiscounts = rentDiscounts.length > 0;
  const hasGeneralDiscounts = generalDiscounts.length > 0;

  const hidePromotion = (): boolean => {
    if (noAvailableBoxes) {
      return true;
    }

    if (isOpened) {
      return true;
    }

    if (hasRentDiscounts) {
      return false;
    }

    if (globalSettings?.showGeneralPromotions) {
      return !hasGeneralDiscounts;
    }

    return true;
  };

  if (isMobile) {
    return (
      <div
        className={classNames('flex flex-col space-y-3 p-4 rounded-lg relative', {
          'cursor-default': noAvailableBoxes,
          'desktop:flex-row desktop:items-center desktop:space-x-5': true,
        })}
      >
        {!hidePromotion() && (
          <div className="absolute top-0 left-0 py-[1px] px-3 bg-accent rounded-br-lg text-white text-xs"> {t('Discount')}</div>
        )}

        <div className="shrink-0 self-center desktop:self-auto desktop:basis-1/13">
          <img
            className={classNames('w-[100px] object-contain desktop:w-[80px]', disabledElement)}
            src={imageUrl || SizeCodeDefaultImage}
            alt="Size image"
          />
        </div>

        <div className={classNames('text-center text-lg font-semibold desktop:text-xl desktop:basis-3/12', disabledElement)}>
          {square} m<sup className="text-sm desktop:text-lg">{sizeCodeType === SizeCodeType.CUBIC_BOX ? 3 : 2}</sup>
          {Boolean(minBoxPrice) && (
            <div className="mt-1 whitespace-nowrap">
              <Trans
                t={t}
                i18nKey={
                  discountedBoxPrice
                    ? '<0>From</0> <1>{{discountedPrice}} {{currencySymbol}}</1><0>/month</0>'
                    : '<0>From</0> <1>{{minPrice}} {{currencySymbol}}</1><0>/month</0>'
                }
                components={[<span key="0" className="text-xs" />, <span key="1" className="text-sm text-accent font-semibold" />]}
                values={{
                  minPrice: minBoxPrice,
                  currencySymbol,
                  discountedPrice: discountedBoxPrice,
                }}
              />
            </div>
          )}
        </div>

        {isDesktop ? (
          <div className={classNames(`${noAvailableBoxes ? 'text-xs' : 'text-sm'} font-normal basis-6/12 min-w-0`, disabledElement)}>
            {noAvailableBoxes
              ? t('There is no available box of this category')
              : `${sizeGroup ? `${sizeGroup}:` : ''} ${getLocalizedString(description)}`}
          </div>
        ) : (
          !noAvailableBoxes && (
            <div className="text-sm text-primaryLight font-normal text-center">
              {sizeGroup ? `${sizeGroup}: ` : ''}
              {getLocalizedString(description)}
            </div>
          )
        )}

        <div className="text-sm text-center text-success font-semibold desktop:text-base shrink-0 whitespace-nowrap">
          {noAvailableBoxes ? <ContactUs square={square} /> : t('Boxes available: {{availableBoxesCount}}', { availableBoxesCount })}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('flex items-center desktop:space-x-5', { 'cursor-default': noAvailableBoxes, 'space-x-1': !isMobile })}>
      <div
        className={classNames('absolute top-0 left-0 py-[1px] px-3 bg-accent rounded-br-lg text-white', {
          hidden: hidePromotion(),
        })}
      >
        {t('Discount')}
      </div>
      <div className="basis-1/13 shrink-0">
        <img className={classNames('w-[80px] object-contain', disabledElement)} src={imageUrl || SizeCodeDefaultImage} alt="Size image" />
      </div>
      <div
        className={classNames(
          'text-xl font-semibold text-center basis-1/2 shrink-0 desktop:basis-3/12 desktop:min-w-[150px] desktop:text-2xl',
          disabledElement,
        )}
      >
        {square} m<sup className="text-sm desktop:text-lg">{sizeCodeType === SizeCodeType.CUBIC_BOX ? 3 : 2}</sup>
        {Boolean(minBoxPrice) && (
          <div className="whitespace-nowrap">
            <Trans
              t={t}
              i18nKey={
                discountedBoxPrice
                  ? '<0>From</0> <1>{{discountedPrice}} {{currencySymbol}}</1><0>/month</0>'
                  : '<0>From</0> <1>{{minPrice}} {{currencySymbol}}</1><0>/month</0>'
              }
              components={[<span key="0" className="text-xs" />, <span key="1" className="text-sm text-accent font-semibold" />]}
              values={{ minPrice: minBoxPrice, currencySymbol, discountedPrice: discountedBoxPrice }}
            />
          </div>
        )}
      </div>
      {isDesktop && (
        <div className={classNames(`${noAvailableBoxes ? 'text-xs' : 'text-sm'} font-normal basis-6/12 min-w-0`, disabledElement)}>
          {noAvailableBoxes
            ? t('There is no available box of this category')
            : `${sizeGroup ? `${sizeGroup}:` : ''} ${getLocalizedString(description)}`}
        </div>
      )}
      {noAvailableBoxes ? (
        <ContactUs square={square} />
      ) : (
        <div className="shrink-0 whitespace-nowrap text-success font-normal text-xs desktop:text-sm">
          {t('Boxes available: {{availableBoxesCount}}', { availableBoxesCount })}
        </div>
      )}
    </div>
  );
});
